import {tokens} from '@pleo-io/telescope'
import {animate} from 'framer-motion'
import React from 'react'
import {useInView} from 'react-intersection-observer'
import styled from 'styled-components'

import useMediaQuery from '@helios/shared/hooks/use-media-query'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import mq from '@helios/shared/utils/media-query'
import RotatingElements from '@helios/website/src/components/rotating-elements'
import useHasMounted from '@helios/website/src/hooks/use-has-mounted'

type Statistic = {
    amount: number
    text: string
    asPercentile: boolean
}

export interface StatisticsProps {
    firstStat: Statistic
    secondStat: Statistic
    thirdStat: Statistic
    sourceText?: React.ReactNode
}

export const Statistics = React.forwardRef<HTMLDivElement, StatisticsProps>(
    ({firstStat, secondStat, thirdStat, sourceText}, ref) => {
        const showSingleItem = useMediaQuery.max(mobileBreakpoint)
        const contentArr = [firstStat, secondStat, thirdStat]

        if (showSingleItem) {
            return (
                <StatsWrapper>
                    <RotatingElementsWrapper>
                        <RotatingElements>
                            {contentArr.map((item, index) => (
                                <React.Fragment key={index}>
                                    <Text variant="hero" translate="no">
                                        {item.asPercentile ? `${item.amount}%` : item.amount}
                                    </Text>
                                    <Text variant="paragraph-large">{item.text}</Text>
                                </React.Fragment>
                            ))}
                        </RotatingElements>
                    </RotatingElementsWrapper>

                    {sourceText}
                </StatsWrapper>
            )
        } else {
            return (
                <StyledSection ref={ref}>
                    <Grid>
                        <Item
                            amount={firstStat.amount}
                            formatAmount={(value) =>
                                firstStat.asPercentile ? `${value}%` : `${value}`
                            }
                            text={firstStat.text}
                        />

                        <Item
                            amount={secondStat.amount}
                            formatAmount={(value) =>
                                secondStat.asPercentile ? `${value}%` : `${value}`
                            }
                            text={secondStat.text}
                        />

                        <Item
                            amount={thirdStat.amount}
                            formatAmount={(value) =>
                                thirdStat.asPercentile ? `${value}%` : `${value}`
                            }
                            text={thirdStat.text}
                        />
                    </Grid>

                    {sourceText}
                </StyledSection>
            )
        }
    }
)

interface ItemProps {
    amount: number
    text: string
    formatAmount: (amount: number) => string
}

const Item = ({amount, text, formatAmount}: ItemProps) => {
    const hasMounted = useHasMounted()
    const [animatedAmount, setAnimatedAmount] = React.useState(0)
    const [numberRef, inView] = useInView({
        threshold: 0.5,
        triggerOnce: true,
    })

    React.useEffect(() => {
        if (!inView) {
            return
        }

        let controls = animate(0, amount, {
            duration: 1,
            onUpdate(value) {
                setAnimatedAmount(Math.round(value))
            },
        })

        return () => {
            controls?.stop()
        }
    }, [amount, inView])

    return (
        <Card>
            <Text variant="hero" ref={numberRef} translate="no">
                {formatAmount(hasMounted ? animatedAmount : amount)}
            </Text>

            <Text color="muted">{text}</Text>
        </Card>
    )
}

const mobileBreakpoint = 'sm'

const StatsWrapper = styled.div`
    display: grid;
    gap: ${tokens.spacing20};
    text-align: center;
`

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing20};
    justify-items: center;

    ${mq.min(mobileBreakpoint)} {
        justify-items: left;
    }
`

const Grid = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
    width: 100%;

    ${mq.min(mobileBreakpoint)} {
        justify-content: space-between;
        grid-template-columns: repeat(3, 1fr);
    }
`

const RotatingElementsWrapper = styled(Section)`
    background: ${tokens.shade200};
`

const Card = styled.div`
    display: grid;
    padding: ${tokens.spacing32};
    text-align: left;
    background: ${tokens.shade200};
    border-radius: ${tokens.arc20};
    row-gap: ${tokens.spacing20};
    align-content: start;

    ${mq.min(mobileBreakpoint)} {
        :not(:first-child) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }

        :not(:last-child) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }

    ${mq.min('md')} {
        padding: ${tokens.spacing60} ${tokens.spacing40};
    }
`
